import { memo, useMemo } from "react";
import { useLocationsMetadata, worldGroupDatum } from "@atlas-int/data";
import { flattenLocationMetadata, computeLocationChangeEffects } from "@atlas-int/data";
import type { TradeDirection, UIView } from "@atlas-int/data/types";
import { vizConstraints } from "@atlas-int/data/schemas";
import { useExploreParams } from "../../../visualization/paramSchemas";
import { useCurrentVizType } from "../../../hooks/useCurrentVizType";
import { LocationDropdown as LocationDropdownBase } from "@atlas-int/controls";
import { vizTypeIcons } from "../../../assets/icons";
import { DropdownRole } from "../DropdownRole";

interface LocationDropdownInput {
  locationSelectionRole: DropdownRole | undefined;
}

export const useChangeImporter = () => {
  const [{ exporter, view, tradeDirection }, setQuery] =
    useExploreParams();
  return (id: string) => {
    const result = computeLocationChangeEffects({
      changingRole: "importer",
      newLocationId: id,
      otherLocationId: exporter,
      view: view as UIView,
      currentTradeDirection: tradeDirection as TradeDirection,
      worldGroupId: worldGroupDatum.groupId,
    });

    const updates: Record<string, unknown> = {
      importer: id,
      tradeDirection: result.tradeDirection,
    };
    if (result.forceOtherLocationId !== undefined) {
      updates.exporter = result.forceOtherLocationId;
    }
    if (result.forceTradeFlow !== undefined) {
      updates.tradeFlow = result.forceTradeFlow;
    }
    setQuery(updates);
  };
};

export const useChangeExporter = () => {
  const [{ importer, view, tradeDirection }, setQuery] =
    useExploreParams();
  return (id: string) => {
    const result = computeLocationChangeEffects({
      changingRole: "exporter",
      newLocationId: id,
      otherLocationId: importer,
      view: view as UIView,
      currentTradeDirection: tradeDirection as TradeDirection,
      worldGroupId: worldGroupDatum.groupId,
    });

    const updates: Record<string, unknown> = {
      exporter: id,
      tradeDirection: result.tradeDirection,
    };
    if (result.forceOtherLocationId !== undefined) {
      updates.importer = result.forceOtherLocationId;
    }
    if (result.forceTradeFlow !== undefined) {
      updates.tradeFlow = result.forceTradeFlow;
    }
    setQuery(updates);
  };
};

const LocationDropdown = ({ locationSelectionRole }: LocationDropdownInput) => {
  const vizType = useCurrentVizType();
  const [{ importer, exporter }] =
    useExploreParams();
  const changeExporter = useChangeExporter();
  const changeImporter = useChangeImporter();
  const locationsState = useLocationsMetadata();
  const metadata = locationsState.status === "success" ? locationsState.data : undefined;
  const loading = locationsState.status === "loading";

  const constraints = vizConstraints[vizType];
  const disableRegions = constraints?.disableRegionSelection ?? false;

  const valueToMatch =
    locationSelectionRole === DropdownRole.LocationImporter
      ? importer
      : exporter;

  const hideServicesPartners = constraints?.hideServicesPartners;
  const flatLocations = useMemo(() => {
    if (loading || !metadata) return [];
    return flattenLocationMetadata(metadata, {
      hideServicesPartners,
    });
  }, [loading, metadata, hideServicesPartners]);

  const handleSelect = (id: string) => {
    if (locationSelectionRole === DropdownRole.LocationImporter) {
      changeImporter(id);
    } else {
      changeExporter(id);
    }
  };

  return (
    <LocationDropdownBase
      locations={flatLocations}
      value={valueToMatch}
      onChange={handleSelect}
      worldIconSrc={vizTypeIcons.geomap}
      disableRegions={disableRegions}
      loading={loading}
      error={locationsState.status === "error"}
    />
  );
};

export default memo(LocationDropdown);
